import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { MetricsColumnFilter, MetricColumn } from '../shared/MetricsColumnFilter';
import { PitchSelector } from '../pitch/PitchSelector';
import { byPitchOptions, usePitchSelection } from '../pitch/pitchSelection';
import type { VideoPitch } from '@/types/pitch';
import { getBrowserIconComponent } from '@/components/metrics/browsers/BrowserIcons';
import '../metrics-table.css';

interface BrowserMetrics {
  browser: string;
  visualizations: number;
  uniqueViews: number;
  plays: number;
  uniquePlays: number;
  playRate: number;
  buttonClicks: number;
  engagement: number;
  /** Retenção (%) no pitch escolhido no seletor. null = sem pitch ou sem dado. */
  pitchRetention: number | null;
  conversions: number;
  revenue: number;
}

interface BrowsersMetricsTableProps {
  data?: any[];
  /** `video.pitches` do consolidado — opções do seletor e índice em `pitchesRetentionPct`. */
  pitches?: VideoPitch[];
}

// Traduz nomes de navegadores para português
const translateBrowserName = (name: string): string => {
  const lower = name.toLowerCase();
  if (lower === 'other' || lower === 'others') return 'Outros';
  if (lower === 'navegador desconhecido' || lower === 'unknown') return 'Desconhecido';
  return name;
};

const defaultColumns: MetricColumn[] = [
  { id: 'visualizations', label: 'Visualizações', visible: true },
  { id: 'uniqueViews', label: 'Únicas', visible: true },
  { id: 'plays', label: 'Plays', visible: true },
  { id: 'uniquePlays', label: 'Plays Únicos', visible: true },
  { id: 'playRate', label: 'Play Rate', visible: true },
  { id: 'buttonClicks', label: 'Cliques', visible: false },
  { id: 'engagement', label: 'Engajamento', visible: true },
  { id: 'pitchRetention', label: 'Pitch', visible: false },
  { id: 'conversions', label: 'Conversões', visible: false },
  { id: 'revenue', label: 'Receita', visible: false },
];

type SortField = keyof BrowserMetrics | null;
type SortDirection = 'asc' | 'desc';

export function BrowsersMetricsTable({ data = [], pitches = [] }: BrowsersMetricsTableProps) {
  useLanguage();
  // Seletor de pitch (só aparece com 2+ pitches): qual pitch preenche a coluna "Pitch".
  const pitchOptions = byPitchOptions(pitches);
  const [pitchId, setPitchId] = usePitchSelection(pitchOptions);
  const pitchIndex = pitches.findIndex((p) => p.id === pitchId);

  // Mapear dados da API para o formato da tabela
  const browserMetrics: BrowserMetrics[] = data.map(browser => ({
    browser: browser.browser || 'Other',
    visualizations: browser.views || 0,
    uniqueViews: browser.uniqueViews || 0,
    plays: browser.plays || 0,
    uniquePlays: browser.uniquePlays || 0,
    playRate: browser.plays && browser.views ? parseFloat(((browser.plays / browser.views) * 100).toFixed(2)) : 0,
    buttonClicks: 0, // TODO: adicionar quando backend enviar
    engagement: parseFloat(browser.engajamento || '0'),
    pitchRetention: pitchIndex >= 0 ? browser.pitchesRetentionPct?.[pitchIndex] ?? null : null,
    conversions: 0, // TODO: adicionar quando backend enviar
    revenue: 0 // TODO: adicionar quando backend enviar
  }));

  const [columns, setColumns] = useState<MetricColumn[]>(defaultColumns);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
  useLanguage();
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-3 w-3 text-[#AEB8C9] group-hover:text-[#6E7A95] transition-colors" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-2 h-3 w-3 text-[#4B7BFF]" />
    ) : (
      <ArrowDown className="ml-2 h-3 w-3 text-[#4B7BFF]" />
    );
  };

  const sortedMetrics = [...browserMetrics].sort((a, b) => {
    if (!sortField) return 0;
    
    const aValue = a[sortField] ?? -1;
    const bValue = b[sortField] ?? -1;
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    return 0;
  });

  // Header cell component for consistency
  const HeaderCell = ({ field, label, className = '' }: { field: SortField; label: string; className?: string }) => { useLanguage(); return ((
    <TableHead 
      className={`text-right bg-[#F7F9FF] ${className}`}
      style={{ 
        borderBottom: '1px solid #EDF1FF',
        padding: '8px 14px',
        height: '44px',
        fontSize: '12px',
        fontWeight: 600,
        color: '#5A6475'
      }}
    >
      <Button
        variant="ghost"
        onClick={() => handleSort(field)}
        className="h-auto p-0 hover:bg-transparent font-semibold ml-auto flex text-[#5A6475] hover:text-[#4B7BFF] group transition-colors"
      >
        {label}
        <SortIcon field={field} />
      </Button>
    </TableHead>
  )); };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-end gap-3">
        {columns.find(c => c.id === 'pitchRetention')?.visible && (
          <PitchSelector options={pitchOptions} value={pitchId} onChange={setPitchId} />
        )}
        <MetricsColumnFilter columns={columns} onColumnsChange={setColumns} />
      </div>
      
      <Card 
        className="metrics-table-container metrics-table-premium overflow-hidden"
        style={{
          background: '#FFFFFF',
          border: '1px solid #EDF1FF',
          borderRadius: '14px',
          boxShadow: '0px 12px 28px rgba(0, 0, 0, 0.04)',
          marginTop: '12px'
        }}
      >
        <div className="metrics-table-wrapper">
          <Table>
            <TableHeader>
              <TableRow className="border-0">
                <TableHead 
                  className="font-semibold w-[180px] bg-[#F7F9FF]"
                  style={{ 
                    borderBottom: '1px solid #EDF1FF',
                    padding: '8px 14px',
                    height: '44px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#5A6475'
                  }}
                >
                  <Button
                    variant="ghost"
                    onClick={() => handleSort('browser')}
                    className="h-auto p-0 hover:bg-transparent font-semibold text-[#5A6475] hover:text-[#4B7BFF] group transition-colors"
                  >
                    {translateInterfaceText("Navegador")}<SortIcon field="browser" />
                  </Button>
                </TableHead>
                {columns.find(c => c.id === 'visualizations')?.visible && (
                  <HeaderCell field="visualizations" label={translateInterfaceText("Visualizações")} className="min-w-[130px]" />
                )}
                {columns.find(c => c.id === 'uniqueViews')?.visible && (
                  <HeaderCell field="uniqueViews" label={translateInterfaceText("Únicas")} className="min-w-[130px]" />
                )}
                {columns.find(c => c.id === 'plays')?.visible && (
                  <HeaderCell field="plays" label={translateInterfaceText("Plays")} className="min-w-[110px]" />
                )}
                {columns.find(c => c.id === 'uniquePlays')?.visible && (
                  <HeaderCell field="uniquePlays" label={translateInterfaceText("Plays Únicos")} className="min-w-[110px]" />
                )}
                {columns.find(c => c.id === 'playRate')?.visible && (
                  <HeaderCell field="playRate" label={translateInterfaceText("Play Rate")} className="min-w-[110px]" />
                )}
                {columns.find(c => c.id === 'buttonClicks')?.visible && (
                  <HeaderCell field="buttonClicks" label={translateInterfaceText("Cliques")} className="min-w-[120px]" />
                )}
                {columns.find(c => c.id === 'engagement')?.visible && (
                  <HeaderCell field="engagement" label={translateInterfaceText("Engajamento")} className="min-w-[120px]" />
                )}
                {columns.find(c => c.id === 'pitchRetention')?.visible && (
                  <HeaderCell field="pitchRetention" label={translateInterfaceText("Pitch")} className="min-w-[120px]" />
                )}
                {columns.find(c => c.id === 'conversions')?.visible && (
                  <HeaderCell field="conversions" label={translateInterfaceText("Conversões")} className="min-w-[120px]" />
                )}
                {columns.find(c => c.id === 'revenue')?.visible && (
                  <HeaderCell field="revenue" label={translateInterfaceText("Receita")} className="min-w-[140px]" />
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedMetrics.map((row, index) => (
                <TableRow 
                  key={index}
                  className={`${index % 2 === 0 ? 'bg-white' : 'bg-[#F9FAFB]'} hover:bg-[#F9FAFB] transition-all duration-150 border-b border-[#E5E7EB]`}
                  style={{ height: '40px' }}
                >
                  <TableCell 
                    className="font-semibold text-[#2C3345] text-[13px]"
                    style={{ padding: '6px 14px' }}
                  >
                    <div className="flex items-center gap-3">
                      {getBrowserIconComponent(row.browser, 24)}
                      <span>{translateBrowserName(row.browser)}</span>
                    </div>
                  </TableCell>
                  {columns.find(c => c.id === 'visualizations')?.visible && (
                    <TableCell className="text-right font-semibold text-[#1C2434] text-[13px]" style={{ padding: '6px 14px' }}>
                      {row.visualizations.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'uniqueViews')?.visible && (
                    <TableCell className="text-right font-semibold text-[#1C2434] text-[13px]" style={{ padding: '6px 14px' }}>
                      {row.uniqueViews.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'plays')?.visible && (
                    <TableCell className="text-right font-semibold text-[#1C2434] text-[13px]" style={{ padding: '6px 14px' }}>
                      {row.plays.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'uniquePlays')?.visible && (
                    <TableCell className="text-right font-semibold text-[#1C2434] text-[13px]" style={{ padding: '6px 14px' }}>
                      {row.uniquePlays.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'playRate')?.visible && (
                    <TableCell className="text-right font-medium text-[#6B7280] text-[13px]" style={{ padding: '6px 14px' }}>
                      {row.playRate}%
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'buttonClicks')?.visible && (
                    <TableCell className="text-right font-semibold text-[#1C2434] text-[13px]" style={{ padding: '6px 14px' }}>
                      {row.buttonClicks.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'engagement')?.visible && (
                    <TableCell className="text-right font-medium text-[#6B7280] text-[13px]" style={{ padding: '6px 14px' }}>
                      {row.engagement}%
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'pitchRetention')?.visible && (
                    <TableCell className="text-right font-medium text-[#6B7280] text-[13px]" style={{ padding: '6px 14px' }}>
                      {row.pitchRetention != null ? `${row.pitchRetention.toFixed(1)}%` : '—'}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'conversions')?.visible && (
                    <TableCell className="text-right font-semibold text-[#1C2434] text-[13px]" style={{ padding: '6px 14px' }}>
                      {row.conversions.toLocaleString(getInterfaceLanguage())}
                    </TableCell>
                  )}
                  {columns.find(c => c.id === 'revenue')?.visible && (
                    <TableCell className="text-right text-[13px]" style={{ padding: '6px 14px' }}>
                      <span className="text-[13px] text-[#0F172A]">R$</span>
                      <span className="text-[14px] font-semibold text-[#0F172A] ml-0.5">
                        {row.revenue.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
